import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { WhatsAppFloat } from "@/components/WhatsAppFloat";
import { SEOWrapper } from "@/components/SEOWrapper";
import { EnhancedStructuredData } from "@/components/EnhancedStructuredData";

// Services Hub sections
import {
  ServicesMethodology,
  ServicesCases,
  ServicesFAQ
} from "@/components/services";
import { ServicesHubComposition } from "@/components/services/ServicesHubComposition";
import { InteriorHead, ActionLink } from "@/components/luminous/interior";
import { ArrowRight } from "lucide-react";

// Reused components from home
import { ForWhoSection } from "@/components/home/ForWhoSection";
import { CTAFinal } from "@/components/home/CTAFinal";
import { usePageSeo } from "@/hooks/usePageSeo";

const ServicesPage = () => {
  const { language, url, esUrl, enUrl, onLanguageChange } = usePageSeo();

  const meta = {
    es: {
      title: "Servicios de SEO y Google Ads para Empresas B2B | Luis ROC - Houston",
      description: "Servicios de SEO técnico, SEO local y Google Ads para fabricantes y proveedores B2B. Sesión de diagnóstico 1:1 con el consultor. Houston, Texas.",
      url: "https://luisroc.com/servicios",
      keywords: [
        "servicios SEO Houston",
        "SEO técnico empresas",
        "SEO local Houston",
        "Google Ads B2B",
        "consultor SEO Texas",
        "marketing digital fabricantes",
        "SEO para proveedores",
        "consultoría Google Ads"
      ]
    },
    en: {
      title: "SEO and Google Ads Services for B2B Companies | Luis ROC - Houston",
      description: "Technical SEO, local SEO, and Google Ads services for B2B manufacturers and service providers. 1:1 diagnosis session with the consultant. Houston, Texas.",
      url: "https://luisroc.com/en/services",
      keywords: [
        "SEO services Houston",
        "technical SEO businesses",
        "local SEO Houston",
        "B2B Google Ads",
        "SEO consultant Texas",
        "digital marketing manufacturers",
        "SEO for providers",
        "Google Ads consulting"
      ]
    }
  };

  const currentMeta = meta[language];

  const faqs = language === "es" ? [
    {
      question: "¿Qué servicios de SEO ofrece Luis ROC?",
      answer: "Ofrezco tres servicios principales: SEO Técnico (optimización de arquitectura web y corrección de errores), SEO Local (posicionamiento en Google Maps y búsquedas locales) y Google Ads / Performance (campañas publicitarias con medición orientada a negocio)."
    },
    {
      question: "¿Cuánto cuesta contratar servicios de SEO?",
      answer: "La inversión depende del alcance, la complejidad del sitio y los objetivos del proyecto. No publico tarifas cerradas: la sesión de diagnóstico 1:1 permite entender la situación antes de proponer un alcance y una inversión concretos."
    },
    {
      question: "¿SEO o Google Ads: qué necesito primero?",
      answer: "Depende de tu urgencia y presupuesto. SEO genera resultados sostenibles a mediano-largo plazo. Google Ads genera leads más rápido pero requiere inversión continua. SEO y Google Ads pueden combinarse según el mercado, objetivo y etapa de crecimiento."
    }
  ] : [
    {
      question: "What SEO services does Luis ROC offer?",
      answer: "I offer three main services: Technical SEO (web architecture optimization and error correction), Local SEO (Google Maps and local search positioning), and Google Ads / Performance (advertising campaigns with business-focused measurement)."
    },
    {
      question: "How much does it cost to hire SEO services?",
      answer: "Investment depends on scope, site complexity and project goals. I don't publish fixed pricing: the 1:1 diagnosis session establishes the situation before proposing a concrete scope and investment."
    },
    {
      question: "SEO or Google Ads: what do I need first?",
      answer: "It depends on your urgency and budget. SEO generates sustainable medium-to-long term results. Google Ads generates leads faster but requires continuous investment. SEO and Google Ads can be combined depending on the market, objective and growth stage."
    }
  ];

  return (
    <SEOWrapper
      title={currentMeta.title}
      description={currentMeta.description}
      keywords={currentMeta.keywords}
      url={url}
      esUrl={esUrl}
      enUrl={enUrl}
      language={language}
      pageType="website"
      structuredData={{
        type: "service",
        data: {
          name: language === 'es' ? "Servicios de SEO y Google Ads" : "SEO and Google Ads Services",
          description: currentMeta.description,
          provider: "Luis ROC",
          areaServed: ["Houston", "Texas", "Florida", "United States"],
          serviceType: ["SEO Técnico", "SEO Local", "Google Ads"],
        },
      }}
      summary={
        language === "es"
          ? "Luis ROC ofrece servicios de SEO y Google Ads para empresas B2B: SEO Técnico, SEO Local y Performance Marketing. Especializado en fabricantes y proveedores de servicios en Houston, Texas y Florida."
          : "Luis ROC offers SEO and Google Ads services for B2B companies: Technical SEO, Local SEO, and Performance Marketing. Specialized in manufacturers and service providers in Houston, Texas, and Florida."
      }
      keyPoints={
        language === "es"
          ? [
              "Servicios: SEO Técnico, SEO Local, Google Ads",
              "Para: Fabricantes y proveedores B2B",
              "Área de servicio: Texas y Florida; sede en Shenandoah, TX",
              "Experiencia en SEO, Search Growth y marketing digital",
              "Sesión de diagnóstico 1:1 con el consultor",
            ]
          : [
              "Services: Technical SEO, Local SEO, Google Ads",
              "For: B2B manufacturers and service providers",
              "Service areas: Texas and Florida; office in Shenandoah, TX",
              "Experience in SEO, Search Growth and digital marketing",
              "1:1 diagnosis session with the consultant",
            ]
      }
    >
      <EnhancedStructuredData
        webpage={{
          name: currentMeta.title,
          description: currentMeta.description,
          url: currentMeta.url,
          keywords: currentMeta.keywords,
        }}
        faqs={faqs}
      />

      <div className="min-h-screen bg-[color:var(--v3-mineral)]">
        <Header language={language} onLanguageChange={onLanguageChange} />

        <main>
          {/* Encabezado interior: qué es, para quién y cómo empezar */}
          <InteriorHead
            crumbs={[
              { label: language === "es" ? "Inicio" : "Home", to: language === "es" ? "/" : "/en" },
              { label: language === "es" ? "Servicios" : "Services" },
            ]}
            eyebrow={language === "es" ? "Servicios" : "Services"}
            title={
              language === "es"
                ? "SEO y Google Ads para empresas B2B"
                : "SEO and Google Ads for B2B companies"
            }
            definition={
              language === "es"
                ? "Tres servicios principales —SEO técnico, SEO local y Google Ads— y un conjunto de capacidades complementarias que se incorporan solo cuando el proyecto las necesita. El punto de partida siempre es entender dónde está el cuello de botella."
                : "Three core services —technical SEO, local SEO and Google Ads— plus complementary capabilities added only when the project needs them. The starting point is always understanding where the bottleneck is."
            }
            audienceLabel={language === "es" ? "Para quién:" : "Who it's for:"}
            audience={
              language === "es"
                ? "Fabricantes, proveedores de servicios y distribuidores B2B en Texas, Florida y el resto de Estados Unidos."
                : "B2B manufacturers, service providers and distributors in Texas, Florida and the rest of the United States."
            }
            actions={
              <>
                <ActionLink to={language === "es" ? "/diagnostico-seo-gratis" : "/en/free-seo-diagnosis"}>
                  {language === "es" ? "Solicitar diagnóstico SEO" : "Request an SEO diagnosis"}
                  <ArrowRight className="h-4 w-4" aria-hidden />
                </ActionLink>
                <ActionLink
                  to={language === "es" ? "/casos-de-exito" : "/en/case-studies"}
                  variant="ghost"
                >
                  {language === "es" ? "Ver proyectos" : "See projects"}
                </ActionLink>
              </>
            }
            facts={
              language === "es"
                ? [
                    { label: "Servicios", value: "SEO técnico, SEO local y Google Ads / Performance." },
                    { label: "Problema", value: "El sitio no genera clientes, no aparece en Google o la inversión publicitaria no tiene retorno claro." },
                    { label: "Diferencia", value: "Trabajo directo con el consultor, métricas claras y cero promesas de posiciones o resultados garantizados." },
                    { label: "Siguiente paso", value: "Reunión inicial gratuita de 30 minutos." },
                  ]
                : [
                    { label: "Services", value: "Technical SEO, local SEO and Google Ads / Performance." },
                    { label: "Problem", value: "The site doesn't generate clients, doesn't appear on Google, or ad spend has no clear return." },
                    { label: "Difference", value: "Direct work with the consultant, clear metrics and no promises of rankings or guaranteed results." },
                    { label: "Next step", value: "Free 30-minute initial consultation." },
                  ]
            }
          />

          {/* Servicios prioritarios en composiciones abiertas + índice secundario */}
          <ServicesHubComposition language={language} />

          {/* Metodología: recorrido con entregables y plazos */}
          <ServicesMethodology language={language} />

          {/* Para quién / No es para ti */}
          <ForWhoSection language={language} />

          {/* Casos relacionados */}
          <ServicesCases language={language} />

          {/* FAQ de servicios */}
          <ServicesFAQ language={language} />

          {/* CTA final */}
          <CTAFinal language={language} />
        </main>

        <Footer language={language} />
        <WhatsAppFloat />
      </div>
    </SEOWrapper>
  );
};

export default ServicesPage;
